import { EMPTY_CALENDAR_CONFIG } from "../calendar/calendarFallback2026";

/**
 * @param {string | Date | undefined} value
 * @returns {string}
 */
export function contentfulDateToISO(value) {
  if (!value) return "";
  if (typeof value === "string") {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * @param {unknown} entry
 * @returns {{ date: string, label: string } | null}
 */
function normalizeClosureEntry(entry) {
  if (typeof entry === "string") {
    const date = contentfulDateToISO(entry);
    return date ? { date, label: "" } : null;
  }

  if (entry && typeof entry === "object" && "date" in entry) {
    const date = contentfulDateToISO(entry.date);
    if (!date) return null;
    const label =
      typeof entry.label === "string" ? entry.label.trim() : "";
    return { date, label };
  }

  return null;
}

/**
 * Parse gymCalendar.closedDates from Contentful JSON.
 * Supports: ["2026-01-11"], [{ "date", "label" }], { "dates": [...] }
 * @param {unknown} value
 * @returns {{ date: string, label: string }[]}
 */
export function parseClosedDatesField(value) {
  if (!value) return [];

  /** @type {unknown[]} */
  let entries = [];

  if (Array.isArray(value)) {
    entries = value;
  } else if (typeof value === "object" && value !== null) {
    if (Array.isArray(value.dates)) {
      entries = value.dates;
    }
  } else if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) entries = parsed;
      } catch {
        entries = trimmed.split(/[\n,]+/).map((part) => part.trim());
      }
    } else {
      entries = trimmed.split(/[\n,]+/).map((part) => part.trim());
    }
  }

  const byDate = new Map();

  entries.forEach((entry) => {
    const closure = normalizeClosureEntry(entry);
    if (closure) {
      byDate.set(closure.date, closure);
    }
  });

  return Array.from(byDate.values()).sort((a, b) =>
    a.date.localeCompare(b.date)
  );
}

/**
 * @param {object} item
 */
export function normalizeCalendarTermEntry(item) {
  const fields = item.fields || item;
  const id =
    item.sys?.id ||
    `${fields.name}-${fields.order}`.toLowerCase().replace(/\s+/g, "-");

  return {
    id,
    name: fields.name?.trim() || "Term",
    season: fields.season?.trim() || "",
    order: Number(fields.order) || 0,
    year: fields.year ? Number(fields.year) : undefined,
    startDate: contentfulDateToISO(fields.startDate),
    endDate: contentfulDateToISO(fields.endDate),
  };
}

/**
 * @param {import('../calendar/calendarFallback2026').CalendarTerm} term
 * @param {number | null | undefined} calendarYear
 */
export function termMatchesCalendarYear(term, calendarYear) {
  if (!calendarYear) return true;
  if (!term.year) return true;
  return term.year === calendarYear;
}

/**
 * Build calendar config from Contentful gymCalendar + calendarTerm entries.
 * @param {object} params
 * @param {object | undefined} params.calendarFields
 * @param {object[]} params.termItems
 */
export function buildCalendarConfig({ calendarFields, termItems = [] }) {
  const calendarYear = calendarFields?.year
    ? Number(calendarFields.year)
    : null;

  const terms = termItems
    .map(normalizeCalendarTermEntry)
    .filter((term) => term.startDate && term.endDate)
    .filter((term) => termMatchesCalendarYear(term, calendarYear))
    .sort((a, b) => {
      const byStart = a.startDate.localeCompare(b.startDate);
      if (byStart !== 0) return byStart;
      return (a.order || 0) - (b.order || 0);
    });

  const closures = parseClosedDatesField(calendarFields?.closedDates);

  return {
    year: calendarYear,
    title: calendarFields?.title?.trim() || "",
    subtitle: calendarFields?.subtitle?.trim() || "",
    intro: calendarFields?.intro?.trim() || "",
    terms,
    closures,
  };
}

export function createEmptyCalendarConfig() {
  return { ...EMPTY_CALENDAR_CONFIG, terms: [], closures: [] };
}
